import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { BookOpen, Clock, Award, CheckCircle, Star, ArrowRight, Play } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  price: number;
  lessons_count: number;
  duration_minutes: number;
  is_active: boolean;
}

export function CourseCatalog() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Courses', icon: BookOpen },
    { id: 'customer_service', name: 'Customer Service', icon: Star },
    { id: 'remote_work', name: 'Remote Work', icon: Clock },
    { id: 'resume_writing', name: 'Resume Writing', icon: Award },
    { id: 'admin_skills', name: 'Admin Skills', icon: CheckCircle },
    { id: 'confidence', name: 'Confidence', icon: Star },
  ];

  const filteredCourses = selectedCategory === 'all'
    ? courses
    : courses.filter((c) => c.category === selectedCategory);

  const benefits = [
    'Text-based micro-learning',
    'Learn at your own pace',
    'Practice exercises included',
    'Certificate of completion',
    'Lifetime access',
    'Mobile-friendly format',
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-100 to-blue-100 px-4 py-2 rounded-full mb-6">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            <span className="text-sm font-semibold text-cyan-900">AI-Powered Micro-Learning</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            AI Career Courses
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn new career skills right from your phone. Text-based lessons delivered at your pace.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = selectedCategory === cat.id;

            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-cyan-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.name}
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-xl"
            >
              <div className="bg-gradient-to-br from-cyan-500 to-blue-500 h-40 relative">
                <div className="absolute inset-0 bg-black/10" />
                <div className="absolute bottom-4 left-6">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                    <BookOpen className="w-4 h-4 text-white" />
                    <span className="text-sm font-semibold text-white">
                      {course.lessons_count} lessons
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900 leading-tight flex-1">
                    {course.title}
                  </h3>
                  <div className="text-2xl font-bold text-cyan-600 ml-2">
                    ${course.price.toFixed(2)}
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {course.description}
                </p>

                <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>{course.duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Award className="w-4 h-4" />
                    <span>Certificate</span>
                  </div>
                </div>

                <button className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all flex items-center justify-center gap-2">
                  <Play className="w-4 h-4" />
                  Enroll Now
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Choose Your Course</h3>
                  <p className="text-white/90 text-sm">
                    Pick a skill you want to build
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Learn via Text</h3>
                  <p className="text-white/90 text-sm">
                    Bite-sized lessons sent to your phone
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Practice & Apply</h3>
                  <p className="text-white/90 text-sm">
                    Real-world exercises and feedback
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Earn Certificate</h3>
                  <p className="text-white/90 text-sm">
                    Show employers what you've mastered
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              What's Included
            </h2>
            <div className="space-y-3">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-cyan-500" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-cyan-50 border-2 border-cyan-200 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <Star className="w-6 h-6 text-cyan-600" />
                <h3 className="font-bold text-gray-900">ResuSwipe+ Members</h3>
              </div>
              <p className="text-sm text-gray-700">
                Get <span className="font-bold text-cyan-600">20% off</span> all courses with your premium subscription
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-10 text-center text-white">
          <BookOpen className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">
            Bundle & Save
          </h2>
          <p className="text-xl mb-6 text-white/90 max-w-2xl mx-auto">
            Buy 3 courses and get the 4th free. Build a complete career toolkit and save 25%.
          </p>
          <button className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg">
            View All Courses
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Course Preview
          </h2>

          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 border-2 border-gray-200">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Lesson 1: Understanding Customer Needs
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>5 minutes</span>
                </div>
                <div className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  <span>Lesson 1 of 8</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                Every customer interaction is an opportunity to build trust. Let's learn the three key questions that help you understand what customers really need...
              </p>
              <div className="text-sm text-gray-500 italic">
                [Preview excerpt - Full lesson continues with examples, practice scenarios, and reflection questions]
              </div>
            </div>

            <button className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all">
              Start This Course
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
